import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';

const OAUTH_MESSAGES = {
  pending_approval: {
    type: 'warning',
    text: "Your account is pending admin approval. You'll be notified once access is granted.",
  },
  account_inactive: {
    type: 'error',
    text: 'Your account has been deactivated. Please contact an administrator.',
  },
  oauth_denied: { type: 'info', text: 'Google sign-in was cancelled.' },
  state_mismatch: { type: 'error', text: 'Authentication failed. Please try again.' },
  oauth_failed: { type: 'error', text: 'Google sign-in failed. Please try again.' },
  no_code: { type: 'error', text: 'Google sign-in failed. Please try again.' },
};

function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');
  const [oauthMessage, setOauthMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error && OAUTH_MESSAGES[error]) {
      setOauthMessage(OAUTH_MESSAGES[error]);
      // Clean the query param from the URL without a page reload
      navigate('/login', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch company logo
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/public/branding');
        if (response.data.company_logo) {
          setCompanyLogo(response.data.company_logo);
        }
      } catch (error) {
        console.log('No company config found');
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, password, name };
      const response = await api.post(endpoint, payload);

      if (!isLogin) {
        toast.success(response.data.message || 'Account created! Awaiting admin approval.');
        setIsLogin(true);
      } else if (response.data.user) {
        // jwt_token cookie is set by the backend (httpOnly) — no localStorage needed
        toast.success('Login successful!');
        navigate('/dashboard');
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Backend initiates the OAuth flow — client ID and secret never touch the frontend
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
          <div className="text-center mb-8">
            {companyLogo ? (
              <div className="flex flex-col items-center gap-3 mb-4">
                <img 
                  src={companyLogo} 
                  alt="GoGet"
                  className="h-16 w-auto object-contain"
                />
                <h1 className="text-2xl font-heading font-bold text-orange-600">
                  ScratchMatch
                </h1>
              </div>
            ) : (
              <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-slate-100 mb-2">
                GoGet ScratchMatch
              </h1>
            )}
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Vehicle Damage Assessment Platform
            </p>
          </div>

          {oauthMessage && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-start gap-2 ${
              oauthMessage.type === 'warning'
                ? 'bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300'
                : oauthMessage.type === 'error'
                ? 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'
                : 'bg-slate-50 border border-slate-200 text-slate-700 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300'
            }`}>
              <span className="mt-0.5 shrink-0">
                {oauthMessage.type === 'warning' ? '⏳' : oauthMessage.type === 'error' ? '⚠️' : 'ℹ️'}
              </span>
              <span>{oauthMessage.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isLogin ? "current-password" : "new-password"}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300 dark:border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-slate-800 text-slate-500">Or</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="mt-4 w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Continue with Google
            </button>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
